import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ServicesService } from 'app/services/services/services.service';
import { SousServiceService } from 'app/services/sous-Service/sous-service.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';

interface SousService {
  id: number;
  name: string;
  image_url?: string;
  listimages?: string[];
  category?: string;
  status?: string;
  displayOrder?: number;
  orderIndex?: number;
  createdAt?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-list-of-sous-services',
  templateUrl: './list-of-sous-services.component.html',
  styleUrls: ['./list-of-sous-services.component.scss']
})
export class ListOfSousServicesComponent implements OnInit {
  Agency: any;
  list: SousService[] = [];
  idAgency: string; // This is actually the service ID (parent service ID)
  agencyId: string | null = null; // For navigation back to agency
  isLoading = false;

  constructor(
    private projetsService: ServicesService,
    private sousService: SousServiceService,
    private awsService: AwscontrolService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.idAgency = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadServiceDetails();
  }

  loadServiceDetails(): void {
    this.isLoading = true;
    this.projetsService.getService(this.idAgency).subscribe({
      next: (data: any) => {
        this.Agency = data;
        // Extract agency ID from the parent service
        if (data.user?.id) {
          this.agencyId = data.user.id.toString();
        } else if (data.userId) {
          this.agencyId = data.userId.toString();
        } else if (data.agencyId) {
          this.agencyId = data.agencyId.toString();
        }

        const rawList = Array.isArray(data?.sous_services) ? data.sous_services : [];
        this.list = rawList;
        this.initializeOrderIndexes();
        this.isLoading = false;
        console.log('Sous-services chargés:', this.list, data);
      },
      error: (err) => {
        console.error('Erreur chargement service:', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de charger les détails du service.',
          icon: 'error'
        });
      }
    });
  }

  private initializeOrderIndexes(): void {
    const hasOrder = this.list.some(s => s.displayOrder !== undefined && s.displayOrder !== null);
    if (hasOrder) {
      // Sort by displayOrder ascending
      this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      // Fallback to createdAt (oldest first)
      this.list.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
    }
    // Assign sequential orderIndex for UI
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
  }

  // ============================================================
  // ORDERING METHODS
  // ============================================================

  private saveAllOrders(): void {
    if (this.list.length === 0) return;
    this.isLoading = true;
    let completed = 0;
    let hasError = false;

    this.list.forEach(sousService => {
      this.sousService.updateSousServiceOrder(sousService.id, sousService.displayOrder!).subscribe({
        next: () => {
          completed++;
          if (completed === this.list.length && !hasError) {
            this.isLoading = false;
            Swal.fire({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 1500,
              icon: 'success',
              title: 'Ordre mis à jour'
            });
          }
        },
        error: (err) => {
          console.error(`Error updating order for sous-service ${sousService.id}:`, err);
          hasError = true;
          this.isLoading = false;
          Swal.fire({
            title: 'Erreur',
            text: 'Impossible de sauvegarder l\'ordre. Rechargement...',
            icon: 'error'
          });
          this.loadServiceDetails(); // reload to revert
        }
      });
    });
  }

  private updateOrdersAndSave(): void {
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
    this.saveAllOrders();
  }

  drop(event: CdkDragDrop<SousService[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.list, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.list[index], this.list[index - 1]] = [this.list[index - 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.list.length - 1) return;
    [this.list[index], this.list[index + 1]] = [this.list[index + 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  // ============================================================
  // DELETE with AWS image removal
  // ============================================================

  deletesousService(sousService: SousService): void {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: `Vous êtes sur le point de supprimer "${sousService.name}". Cette action est irréversible !`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.isLoading = true;
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => Swal.showLoading()
        });

        // 1. Delete all images from AWS
        this.deleteImagesFromS3(sousService)
          .then(() => {
            // 2. Delete sous-service from backend
            return this.sousService.deleteSousService(sousService.id).toPromise();
          })
          .then(() => {
            Swal.close();
            Swal.fire({
              title: 'Supprimé !',
              text: 'Le sous-service et ses images ont été supprimés.',
              icon: 'success',
              timer: 1500,
              showConfirmButton: false
            });
            // Refresh the list
            this.loadServiceDetails();
          })
          .catch((err) => {
            Swal.close();
            console.error('Erreur lors de la suppression:', err);
            // If image deletion fails, we still try to delete the sous-service
            this.sousService.deleteSousService(sousService.id).subscribe({
              next: () => {
                Swal.fire({
                  title: 'Supprimé !',
                  text: 'Le sous-service a été supprimé (images non supprimées).',
                  icon: 'success',
                  timer: 1500,
                  showConfirmButton: false
                });
                this.loadServiceDetails();
              },
              error: (err2) => {
                Swal.fire({
                  title: 'Erreur',
                  text: 'La suppression a échoué.',
                  icon: 'error'
                });
                console.error(err2);
                this.isLoading = false;
              }
            });
          });
      }
    });
  }

  private async deleteImagesFromS3(sousService: SousService): Promise<void> {
    const imageUrls: string[] = [];

    // Main image
    if (sousService.image_url && sousService.image_url.trim() !== '') {
      imageUrls.push(sousService.image_url);
    }

    // Gallery images (listimages)
    if (sousService.listimages && Array.isArray(sousService.listimages)) {
      sousService.listimages.forEach((url: string) => {
        if (url && url.trim() !== '') {
          imageUrls.push(url);
        }
      });
    }

    if (imageUrls.length === 0) {
      return;
    }

    const deletePromises = imageUrls.map(url =>
      this.awsService.deleteFile(url).toPromise().catch(err => {
        console.warn(`Failed to delete image ${url}:`, err);
        return Promise.resolve();
      })
    );

    await Promise.all(deletePromises);
  }

  // ============================================================
  // NAVIGATION
  // ============================================================

  navigateBack(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-Services', this.agencyId]);
    } else {
      // Fallback to previous page
      this.location.back();
    }
  }
}